import { useEffect, useState } from 'react';
import type { Content } from '@neopolis/engine';

import { T } from '../textes-interface.js';
import type { AnalyseDeClasse, ClientProf } from './api-prof.js';

const dureeLisible = (secondes: number | null): string => {
  if (secondes === null) return '—';
  const minutes = Math.floor(secondes / 60);
  const reste = Math.round(secondes % 60);
  return minutes === 0 ? `${reste} s` : `${minutes} min ${String(reste).padStart(2, '0')}`;
};

/** Une barre de trois niveaux, pour lire un questionnaire d'un coup d'œil. */
function TroisNiveaux({
  titre,
  niveaux,
}: {
  titre: string;
  niveaux: { peu: number; moyen: number; bien: number };
}) {
  const total = niveaux.peu + niveaux.moyen + niveaux.bien;

  return (
    <div>
      <p className="font-semibold text-slate-800">{titre}</p>
      <dl className="mt-1 flex flex-wrap gap-4">
        {(
          [
            ['peu', T.questionnaire.peu],
            ['moyen', T.questionnaire.moyen],
            ['bien', T.questionnaire.bien],
          ] as const
        ).map(([niveau, libelle]) => (
          <div key={niveau} className="flex items-baseline gap-1">
            <dt className="text-sm text-slate-600">{libelle}</dt>
            <dd className="text-lg font-bold tabular-nums text-slate-900">{niveaux[niveau]}</dd>
          </div>
        ))}
        <div className="flex items-baseline gap-1">
          <dt className="text-sm text-slate-600">{T.prof.analyseIlots}</dt>
          <dd className="text-lg font-bold tabular-nums text-slate-900">{total}</dd>
        </div>
      </dl>
    </div>
  );
}

/**
 * Analyse de séance.
 *
 * Ce que l'enseignant regarde une fois la classe partie : combien de temps a
 * pris chaque étape, où les îlots sont restés bloqués, ce qu'ils se sont
 * trompés, et ce qu'ils en ont dit.
 *
 * Tout est agrégé côté serveur : l'état des îlots ne sort jamais de la base.
 */
export function AnalyseDeSeance({
  content,
  client,
  classCode,
  pin,
  onQuitter,
}: {
  content: Content;
  client: ClientProf;
  classCode: string;
  pin: string;
  onQuitter: () => void;
}) {
  const [analyse, setAnalyse] = useState<AnalyseDeClasse | null>(null);

  useEffect(() => {
    void client.analyser(classCode, pin).then(setAnalyse);
  }, [client, classCode, pin]);

  const titreChapitre = (id: string): string =>
    content.chapitres.find((c) => c.id === id)?.titre ?? id;

  const libelleType = (type: string): string =>
    type === 'erreur_classement' ? T.prof.erreurClassement : T.prof.erreurChaine;

  return (
    <section className="space-y-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900">
            {T.prof.analyse} — {classCode}
          </h2>
          {analyse && (
            <p className="text-sm text-slate-600">
              {T.prof.analyseIlots} : {analyse.seance.ilots}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onQuitter}
          className="min-h-touch rounded-xl bg-white px-4 font-semibold text-slate-700 shadow-sm"
        >
          {T.prof.quitterAnalyse}
        </button>
      </header>

      <section className="overflow-x-auto rounded-2xl border-2 border-slate-200 bg-white">
        <h3 className="p-4 pb-0 font-bold text-slate-900">{T.prof.tempsParEtape}</h3>
        <table className="mt-2 w-full border-collapse text-left">
          <thead>
            <tr className="border-b-2 border-slate-300">
              <th className="p-3">{T.prof.colonneChapitre}</th>
              <th className="p-3">{T.prof.colonneEtape}</th>
              <th className="p-3 text-right">{T.prof.colonneTempsMedian}</th>
              <th className="p-3 text-right">{T.prof.colonneArrives}</th>
              <th className="p-3 text-right">{T.prof.colonneTermines}</th>
            </tr>
          </thead>
          <tbody>
            {(analyse?.seance.etapes ?? []).map((etape) => (
              <tr key={`${etape.chapitre}-${etape.etape}`} className="border-b border-slate-200">
                <td className="p-3 text-slate-700">{titreChapitre(etape.chapitre)}</td>
                <td className="p-3 font-medium text-slate-900">{etape.titre}</td>
                <td className="p-3 text-right tabular-nums">
                  {dureeLisible(etape.tempsMedianSecondes)}
                </td>
                <td className="p-3 text-right tabular-nums">{etape.ilotsArrives}</td>
                <td className="p-3 text-right tabular-nums">{etape.ilotsTermines}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="rounded-2xl border-2 border-slate-200 bg-white p-4">
        <h3 className="font-bold text-slate-900">{T.prof.blocages}</h3>
        {analyse && analyse.seance.blocages.length === 0 ? (
          <p className="mt-2 text-slate-600">{T.prof.aucunBlocage}</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {(analyse?.seance.blocages ?? []).map((etape) => (
              <li
                key={`${etape.chapitre}-${etape.etape}`}
                className="flex flex-wrap items-baseline gap-x-3 rounded-xl bg-amber-50 p-3"
              >
                <span className="font-semibold text-slate-900">{etape.titre}</span>
                <span className="text-sm text-slate-600">{titreChapitre(etape.chapitre)}</span>
                <span className="ml-auto font-bold tabular-nums text-amber-800">
                  {etape.ilotsBloques} {T.prof.ilotsBloques}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-2xl border-2 border-slate-200 bg-white p-4">
        <h3 className="font-bold text-slate-900">{T.prof.erreursFrequentes}</h3>
        {analyse && analyse.seance.erreurs.length === 0 ? (
          <p className="mt-2 text-slate-600">{T.prof.aucuneErreur}</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {(analyse?.seance.erreurs ?? []).map((erreur) => (
              <li
                key={`${erreur.type}-${erreur.detail}`}
                className="flex flex-wrap items-baseline gap-x-3 rounded-xl bg-slate-50 p-3"
              >
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {libelleType(erreur.type)}
                </span>
                <span className="font-medium text-slate-900">{erreur.libelle}</span>
                <span className="ml-auto tabular-nums text-slate-700">
                  {erreur.occurrences} {T.prof.occurrences} · {erreur.ilots} {T.prof.surIlots}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-2xl border-2 border-slate-200 bg-white p-4">
        <h3 className="font-bold text-slate-900">{T.prof.retoursQuestionnaire}</h3>

        {(analyse?.questionnaires ?? []).map((reponses) => {
          const total = reponses.compris.peu + reponses.compris.moyen + reponses.compris.bien;
          if (total === 0) {
            return null;
          }

          return (
            <article key={reponses.chapitre} className="mt-3 rounded-xl bg-slate-50 p-3">
              <h4 className="font-semibold text-slate-900">{titreChapitre(reponses.chapitre)}</h4>
              <div className="mt-2 space-y-2">
                <TroisNiveaux titre={T.questionnaire.compris} niveaux={reponses.compris} />
                <TroisNiveaux titre={T.questionnaire.aime} niveaux={reponses.aime} />
              </div>

              {reponses.difficultes.length > 0 && (
                <div className="mt-3">
                  <p className="font-semibold text-slate-800">{T.prof.difficultes}</p>
                  <ul className="mt-1 list-inside list-disc text-slate-800">
                    {reponses.difficultes.map((texte) => (
                      <li key={texte}>{texte}</li>
                    ))}
                  </ul>
                </div>
              )}
            </article>
          );
        })}

        {analyse &&
          analyse.questionnaires.every(
            (r) => r.compris.peu + r.compris.moyen + r.compris.bien === 0,
          ) && <p className="mt-2 text-slate-600">{T.prof.aucuneReponse}</p>}
      </section>
    </section>
  );
}
